import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CircleAlert } from "lucide-react";
import { cn } from "./utils";

/**
 * InfoTooltip — 感叹号图标 + 桌面 hover + 移动端 tap-to-toggle 提示
 *
 * 不依赖 Radix（其 hover 触发在触屏上不可用）。核心行为：
 *  - 桌面：`onPointerEnter / onPointerLeave` 控制显示
 *  - 移动端：`onClick` toggle；点击容器外的位置 / Esc 自动关闭
 *  - `onPointerDown` 区分 mouse / touch 来源，避免 click 后再 leave 把已经
 *    通过 tap 打开的 tooltip 立刻关闭
 *
 * 定位策略（关键）：
 *  - tooltip 子元素使用 `position: fixed`，不受父容器 overflow / transform 剪裁，
 *    也不被 modal z-index 遮挡；
 *  - 每次 `open` 变 true 时，tooltip 以 `visibility: hidden` 渲染出来以拿到真实
 *    尺寸，`useLayoutEffect` 同步测量 + 直接操作 DOM 样式（而非经由 state），
 *    这样无触发二次 render，也避开 react-hooks/set-state-in-effect；
 *  - 水平 clamp 到 `[VP_PAD, vw - tipWidth - VP_PAD]`，保证移动端小屏不溢出；
 *  - 垂直：优先 `placement`，若该方向空间不足则 flip 到对侧；
 *  - scroll/resize 时重新计算位置（capture 阶段，兼容 modal 内部滚动容器）。
 *
 * 用例：
 * ```tsx
 * <span>立即生效 <InfoTooltip content={t('foo.hint')} /></span>
 * ```
 */
export interface InfoTooltipProps {
  /** 提示内容（纯文本或 ReactNode） */
  content: React.ReactNode;
  /** 触发图标尺寸（lucide 像素值），默认 14 */
  iconSize?: number;
  /** 弹出方向的首选（空间不足时会自动 flip 到对侧），默认 top */
  placement?: "top" | "bottom" | "left" | "right";
  /** 触发图标的 className（颜色等） */
  triggerClassName?: string;
  /** 弹层最大宽度（px），默认 240；同时会被视口宽度 clamp */
  maxWidthPx?: number;
  /** aria-label，默认 "More info" */
  ariaLabel?: string;
}

/** 视口内边距（防止 tooltip 贴边） */
const VP_PAD = 12;
/** 图标与 tooltip 的间距 */
const GAP = 8;

type Placement = "top" | "bottom" | "left" | "right";

/**
 * 基于触发按钮与 tooltip 的 boundingRect 算出最终 `top / left`。
 * 垂直/水平方向均在空间不足时 flip 到对侧；另一轴 clamp 到视口内。
 *
 * 抽成纯函数，避免 useLayoutEffect / resize handler 共用时触发
 * react-hooks/set-state-in-effect 告警。
 */
function computeTooltipPos(
  btnRect: DOMRect,
  tipRect: DOMRect,
  vw: number,
  vh: number,
  preferred: Placement
): { top: number; left: number } {
  let finalPlacement: Placement = preferred;

  if (preferred === "top" || preferred === "bottom") {
    const roomAbove = btnRect.top;
    const roomBelow = vh - btnRect.bottom;
    if (
      preferred === "top" &&
      roomAbove < tipRect.height + GAP &&
      roomBelow > roomAbove
    ) {
      finalPlacement = "bottom";
    } else if (
      preferred === "bottom" &&
      roomBelow < tipRect.height + GAP &&
      roomAbove > roomBelow
    ) {
      finalPlacement = "top";
    }
  } else {
    const roomLeft = btnRect.left;
    const roomRight = vw - btnRect.right;
    if (
      preferred === "left" &&
      roomLeft < tipRect.width + GAP &&
      roomRight > roomLeft
    ) {
      finalPlacement = "right";
    } else if (
      preferred === "right" &&
      roomRight < tipRect.width + GAP &&
      roomLeft > roomRight
    ) {
      finalPlacement = "left";
    }
  }

  let top: number;
  let left: number;

  if (finalPlacement === "top" || finalPlacement === "bottom") {
    // 水平居中于按钮，再 clamp 到视口
    const centerX = btnRect.left + btnRect.width / 2 - tipRect.width / 2;
    left = Math.max(
      VP_PAD,
      Math.min(centerX, vw - tipRect.width - VP_PAD)
    );
    top =
      finalPlacement === "top"
        ? Math.max(VP_PAD, btnRect.top - tipRect.height - GAP)
        : Math.min(vh - tipRect.height - VP_PAD, btnRect.bottom + GAP);
  } else {
    // 垂直居中于按钮，再 clamp 到视口
    const centerY = btnRect.top + btnRect.height / 2 - tipRect.height / 2;
    top = Math.max(
      VP_PAD,
      Math.min(centerY, vh - tipRect.height - VP_PAD)
    );
    left =
      finalPlacement === "left"
        ? Math.max(VP_PAD, btnRect.left - tipRect.width - GAP)
        : Math.min(vw - tipRect.width - VP_PAD, btnRect.right + GAP);
  }

  return { top, left };
}

export function InfoTooltip({
  content,
  iconSize = 14,
  placement = "top",
  triggerClassName,
  maxWidthPx = 240,
  ariaLabel = "More info",
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  // 区分本次交互是 touch 还是 mouse；touch 触发后忽略后续 hover 事件
  const lastPointerIsTouchRef = useRef(false);

  /**
   * 实际测量并把位置写入 tooltip DOM 样式。
   * 直接操作 DOM（而非 setState）是为了避免 useLayoutEffect 内 setState
   * 触发的 cascade render 警告，且对 tooltip 这种纯展示层完全可控。
   */
  const positionTooltip = useCallback(() => {
    const wrap = wrapRef.current;
    const tip = tooltipRef.current;
    if (!wrap || !tip) return;
    const btnRect = wrap.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const { top, left } = computeTooltipPos(
      btnRect,
      tipRect,
      window.innerWidth,
      window.innerHeight,
      placement
    );
    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
    tip.style.visibility = "visible";
  }, [placement]);

  // 打开时先拿真实尺寸再同步定位，用户不会看到位置跳动
  useLayoutEffect(() => {
    if (!open) return;
    positionTooltip();
  }, [open, content, positionTooltip]);

  // 打开状态下：滚动 / 窗口尺寸变化 → 重算（capture 阶段覆盖 modal 内滚动容器）
  useEffect(() => {
    if (!open) return;
    const handler = () => positionTooltip();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, positionTooltip]);

  // 点击外部 / Esc 关闭
  useEffect(() => {
    if (!open) return;
    const handleDocPointerDown = (e: PointerEvent) => {
      const wrap = wrapRef.current;
      const tip = tooltipRef.current;
      const target = e.target as Node | null;
      if (!target) return;
      if (wrap?.contains(target)) return;
      if (tip?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handleDocPointerDown, true);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("pointerdown", handleDocPointerDown, true);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cn(
          "inline-flex items-center justify-center rounded-full",
          "text-current focus:outline-none focus-visible:ring-2 focus-visible:ring-current/30",
          "hover:opacity-80 active:opacity-90 transition-opacity",
          triggerClassName
        )}
        onPointerDown={(e) => {
          lastPointerIsTouchRef.current = e.pointerType !== "mouse";
        }}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setOpen(false);
        }}
        onClick={(e) => {
          // 桌面 hover 已经把 open 置为 true；touch 场景下用 click 切换
          if (lastPointerIsTouchRef.current) {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (!lastPointerIsTouchRef.current) setOpen(false);
        }}
      >
        <CircleAlert
          style={{ width: iconSize, height: iconSize }}
          strokeWidth={2.5}
          aria-hidden
        />
      </button>
      {open && (
        <span
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            "fixed z-[300] px-3 py-2 rounded-md text-xs leading-relaxed",
            "bg-slate-900 text-white shadow-lg dark:bg-[#0E0F11] dark:border dark:border-[#D2F0E1]/10",
            "whitespace-normal break-words pointer-events-none"
          )}
          style={{
            // 首次渲染先隐藏，等 useLayoutEffect 拿到真实尺寸后
            // positionTooltip 会把 top/left/visibility 写入 DOM
            top: -9999,
            left: -9999,
            visibility: "hidden",
            maxWidth: `min(${maxWidthPx}px, calc(100vw - ${VP_PAD * 2}px))`,
            width: "max-content",
          }}
        >
          {content}
        </span>
      )}
    </span>
  );
}

export default InfoTooltip;
